import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "../api";
import { Alert, Badge, Button, Card, Chips, ErrorBox, Empty, FormError, Loading, Modal, PageHeader, Reg, SelectInput, TextArea, TextInput, useToast } from "../ui";
import { CustomerFormModal, DeleteRecordButton } from "../components";
import { DeptBadge, EnquiryStatusBadge, date, dateTime } from "../format";
import { EXCLUDED_SERVICES } from "../../shared/labels";

const EXCLUDED_RE = /\bmot\b|tyre|tire|auto.?electric|electric vehicle|\bev\b|hybrid battery/i;

export default function Enquiries() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const q = useQuery({ queryKey: ["enquiries", status], queryFn: () => api.get(`/enquiries${qs({ status })}`) });
  const [logging, setLogging] = useState(!!params.get("new"));
  return (
    <>
      <PageHeader title="Enquiries" sub="Website enquiries arrive here automatically. Phone and walk-in enquiries can be logged by staff."
        actions={<Button variant="primary" onClick={() => setLogging(true)}>+ Log enquiry</Button>} />
      <Chips value={status} onChange={(v) => setParams(v ? { status: v } : {}, { replace: true })}
        options={[{ value: "", label: "Open" }, { value: "new", label: "New" }, { value: "contacted", label: "Contacted" }, { value: "quoted", label: "Quoted" }, { value: "booked", label: "Booked" }, { value: "closed", label: "Closed" }, { value: "spam", label: "Spam" }]} />
      {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} retry={() => q.refetch()} /> : !q.data.enquiries.length ? <Card><Empty title="No enquiries here" /></Card> : (
        <div className="stack">{q.data.enquiries.map((e: any) => <EnquiryCard key={e.id} e={e} />)}</div>
      )}
      {logging && <LogEnquiryModal onClose={() => { setLogging(false); setParams({}, { replace: true }); }} />}
    </>
  );
}

function EnquiryCard({ e }: { e: any }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [linking, setLinking] = useState(false);
  const update = useMutation({
    mutationFn: (body: any) => api.patch(`/enquiries/${e.id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["enquiries"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
    onError: (err) => toast("error", (err as Error).message),
  });
  const excluded = EXCLUDED_RE.test(`${e.service ?? ""} ${e.message ?? ""}`);
  const next = new URLSearchParams({ ...(e.customer_id ? { customer: e.customer_id } : {}), ...(e.vehicle_id ? { vehicle: e.vehicle_id } : {}), enquiry: e.id, department: e.department ?? "mechanical" }).toString();
  return (
    <Card title={<>{e.name} <EnquiryStatusBadge s={e.status} /> {e.source === "website" ? <Badge tone="blue">Website</Badge> : <Badge>{e.source.replace("_", "-")}</Badge>}</>}
      actions={<span className="small muted">{dateTime(e.received_at)}</span>}>
      <div className="grid grid-2">
        <dl className="kv">
          <dt>Phone</dt><dd>{e.phone ? <a href={`tel:${e.phone}`}>{e.phone}</a> : "—"}</dd>
          {e.email && <><dt>Email</dt><dd>{e.email}</dd></>}
          <dt>Vehicle</dt><dd><Reg reg={e.registration} /></dd>
          <dt>Service</dt><dd>{e.service ?? "—"} <DeptBadge d={e.department} /></dd>
          <dt>Preferred date</dt><dd>{e.preferred_date ? date(e.preferred_date) : "—"}</dd>
          <dt>Customer</dt><dd>{e.customer_id ? <Link to={`/customers/${e.customer_id}`}>{e.customer_name}</Link> : <span className="muted">Not linked yet</span>}</dd>
        </dl>
        <div>{e.message ? <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{e.message}</p> : <span className="muted">No message</span>}</div>
      </div>
      {excluded && <div style={{ marginTop: 12 }}><Alert kind="warn" title="May be a service we don't offer">We don't do {EXCLUDED_SERVICES.join(", ")}. Check before quoting.</Alert></div>}
      <div className="row" style={{ marginTop: 12 }}>
        {!e.customer_id && <Button variant="primary" onClick={() => setLinking(true)}>Create / find customer</Button>}
        {e.customer_id && <Link className="btn btn-primary" to={`/quotes/new?${next}`}>Quote</Link>}
        {e.customer_id && <Link className="btn btn-secondary" to={`/bookings?new=1&date=${e.preferred_date ?? ""}&${next}`}>Book in</Link>}
        {e.status === "new" && <Button onClick={() => update.mutate({ status: "contacted" })}>Mark contacted</Button>}
        {!["closed", "spam"].includes(e.status) && <Button variant="ghost" onClick={() => update.mutate({ status: "closed" })}>Close</Button>}
        {e.status === "new" && e.source === "website" && <Button variant="ghost" onClick={() => update.mutate({ status: "spam" })}>Spam</Button>}
        <DeleteRecordButton kind="enquiry" id={e.id} />
      </div>
      {linking && <CustomerFormModal initial={{ name: e.name, phone: e.phone ?? "", email: e.email ?? "" }} onClose={() => setLinking(false)}
        onSaved={(customerId) => {
          setLinking(false);
          // The server links the enquiry's vehicle by registration (or adds it once) in the same save.
          update.mutate({ customerId }, {
            onSuccess: (r: any) => r.vehicleNote ? toast("error", r.vehicleNote)
              : toast("success", r.vehicleId ? (r.vehicleCreated ? `Customer linked and vehicle ${e.registration} added` : "Customer and saved vehicle linked") : "Customer linked — add the vehicle when quoting or booking"),
          });
        }} />}
    </Card>
  );
}

function LogEnquiryModal({ onClose }: { onClose: () => void }) {
  const [f, setF] = useState({ source: "phone", name: "", phone: "", email: "", registration: "", service: "", department: "", preferredDate: "", message: "" });
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => api.post("/enquiries", { ...f, department: f.department || null, preferredDate: f.preferredDate || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["enquiries"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast("success", "Enquiry logged"); onClose(); },
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title="Log enquiry" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!f.name.trim()} onClick={() => m.mutate()}>Save</Button></>}>
      <div className="form-grid">
        <SelectInput label="Source" value={f.source} onChange={set("source")} options={[{ value: "phone", label: "Phone call" }, { value: "walk_in", label: "Walk-in" }, { value: "manual", label: "Other" }]} />
        <TextInput label="Name" required value={f.name} onChange={set("name")} />
        <TextInput label="Phone" type="tel" value={f.phone} onChange={set("phone")} />
        <TextInput label="Email" type="email" value={f.email} onChange={set("email")} />
        <TextInput label="Registration" className="reg-input" value={f.registration} onChange={(e) => setF({ ...f, registration: e.target.value.toUpperCase() })} />
        <SelectInput label="Department" value={f.department} onChange={set("department")} placeholder="Not sure" options={[{ value: "mechanical", label: "Mechanical" }, { value: "bodywork", label: "Bodywork" }]} />
        <TextInput label="Service" value={f.service} onChange={set("service")} />
        <TextInput label="Preferred date" type="date" value={f.preferredDate} onChange={set("preferredDate")} />
        <TextArea label="Details" full value={f.message} onChange={set("message")} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}
